import { useCallback, useState } from "react";
import { createViewAuditLog, type TAuditLogPage, type TAuditLogFacetOptions } from "@/ui/views";
import { NOOP } from "@/utils/utils";
import type { TAsyncState } from "@/store";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { SortingState } from "@tanstack/react-table";
import type { TAuditLogQuery } from "@/apis/proda_finance";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";

/** Column ids whose value is a date-range object → {col}_from/{col}_to. */
const DATE_RANGE_COLUMNS: Record<string, [keyof TAuditLogQuery, keyof TAuditLogQuery]> = {
    eventAt: ["event_at_from", "event_at_to"],
};

/** Column ids mapped to a repeated (array) query param. */
const MULTISELECT_COLUMNS: Record<string, keyof TAuditLogQuery> = {
    action: "action",
    category: "category",
    deal_id: "deal_id",
    service_provider_id: "service_provider_id",
    user_id: "user_id",
    company_id: "company_id",
};

/** Column ids mapped to a scalar text query param. */
const TEXT_COLUMNS: Record<string, keyof TAuditLogQuery> = {
    tableName: "table_name",
    changedFields: "changed_fields",
    rowId: "row_id",
};

export function toAuditLogQuery(input: TServerTableQueryInput, _isGlobal: boolean): TAuditLogQuery {
    const query: TAuditLogQuery = {
        page: input.page,
        page_size: input.pageSize,
    };
    for (const f of input.columnFilters) {
        if (f.id === "period") {
            const vals = (f.value as string[]) ?? [];
            if (vals.length > 0) query.periods = vals;
            continue;
        }
        if (f.id in DATE_RANGE_COLUMNS) {
            const keys = DATE_RANGE_COLUMNS[f.id]!;
            const v = f.value as { from?: string; to?: string } | undefined;
            if (v?.from) (query[keys[0]] as string) = v.from;
            if (v?.to) (query[keys[1]] as string) = v.to;
            continue;
        }
        if (f.id in MULTISELECT_COLUMNS) {
            const key = MULTISELECT_COLUMNS[f.id]!;
            (query[key] as string[]) = (f.value as string[]) ?? [];
            continue;
        }
        if (f.id in TEXT_COLUMNS) {
            const key = TEXT_COLUMNS[f.id]!;
            (query[key] as string) = String(f.value ?? "");
            continue;
        }
    }
    const sort: SortingState = input.sorting;
    const firstSort = sort[0];
    if (firstSort) {
        query.sort_by = firstSort.id;
        query.sort_dir = firstSort.desc ? "desc" : "asc";
    }
    return query;
}

const EMPTY_FACETS: TAuditLogFacetOptions = {
    deals: [],
    businessPartners: [],
    periods: [],
    users: [],
    companies: [],
};

export function wireAuditLog({ api, useAppStore }: TCompositionDeps) {
    const ViewAuditLog = createViewAuditLog({
        useCases: () => {
            const subgroupLabels = useAppStore((s) => s.user?.subgroupLabels ?? {});

            const [auditState, setAuditState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: TAuditLogPage | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const [facetOptions, setFacetOptions] = useState<TAuditLogFacetOptions>(EMPTY_FACETS);

            const fetchData = useCallback(async (query: TAuditLogQuery, signal?: AbortSignal) => {
                setAuditState((prev) => ({ ...prev, status: "pending" }));
                try {
                    const response = await api.auditLog.list(query, signal);
                    setAuditState({
                        status: "fulfilled",
                        data: {
                            items: response.items.map((e) => ({
                                id: e.id,
                                eventAt: e.event_at,
                                tableName: "",
                                action: e.action,
                                rowId: null,
                                companyId: null,
                                companyName: null,
                                userId: null,
                                userName: e.user_name,
                                userEmail: e.user_email,
                                emulatedSubgroup: e.emulated_subgroup ?? null,
                                oldData: null,
                                newData: null,
                                changedFields: e.changed_fields,
                                description: e.description,
                                category: e.category,
                                changes: e.changes,
                                dealName: e.deal_name ?? null,
                                serviceProviderName: e.service_provider_name ?? null,
                                period: e.period ?? null,
                                fileId: e.file_id ?? null,
                                fileName: e.file_name ?? null,
                            })),
                            total: response.total,
                            totalUnfiltered: response.total_unfiltered ?? response.total,
                            page: response.page,
                            pageSize: response.page_size,
                            hasMore: response.has_more ?? false,
                        },
                        error: null,
                    });
                } catch (error) {
                    // Swallow abort errors — the superseding request owns the loading state.
                    if (error instanceof DOMException && error.name === "AbortError") return;
                    setAuditState({
                        status: "rejected",
                        data: null,
                        error: error instanceof Error ? error : new Error(String(error)),
                    });
                }
            }, []);

            const table = useServerTable<TAuditLogQuery>({
                pageSize: 50,
                initialSort: [],
                toQuery: useCallback(
                    (input: TServerTableQueryInput) => toAuditLogQuery(input, false),
                    [],
                ),
                fetchData,
            });

            const onMount = useCallback(() => {
                api.auditLog
                    .filterOptions()
                    .then((opts) =>
                        setFacetOptions({
                            deals: opts.deals,
                            businessPartners: opts.business_partners,
                            periods: opts.periods,
                            users: opts.users,
                            companies: opts.companies,
                        }),
                    )
                    .catch(() => undefined);
            }, []);

            return {
                onMount,
                onUnmount: NOOP,
                useAuditLogState: () => auditState as TAsyncState<(typeof auditState)["data"]>,
                onPageChange: table.setPage,
                currentPage: () => table.page,
                isGlobal: false,
                subgroupLabels,
                onDownloadFile: useCallback(async (fileId: string) => {
                    await api.files.downloadFile(fileId, "processed");
                }, []),
                columnFilters: table.columnFilters,
                onColumnFiltersChange: table.onColumnFiltersChange,
                sorting: table.sorting,
                onSortingChange: table.onSortingChange,
                facetOptions,
            };
        },
    });

    const ViewGlobalAuditLog = createViewAuditLog({
        useCases: () => {
            const subgroupLabels = useAppStore((s) => s.user?.subgroupLabels ?? {});

            const [auditState, setAuditState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: TAuditLogPage | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const [facetOptions, setFacetOptions] = useState<TAuditLogFacetOptions>(EMPTY_FACETS);

            const fetchData = useCallback(async (query: TAuditLogQuery, signal?: AbortSignal) => {
                setAuditState((prev) => ({ ...prev, status: "pending" }));
                try {
                    const response = await api.auditLog.listGlobal(query, signal);
                    setAuditState({
                        status: "fulfilled",
                        data: {
                            items: response.items.map((e) => ({
                                id: e.id,
                                eventAt: e.event_at,
                                tableName: e.table_name,
                                action: e.action,
                                rowId: e.row_id,
                                companyId: e.company_id,
                                companyName: e.company_name,
                                userId: e.user_id,
                                userName: e.user_name,
                                userEmail: e.user_email,
                                emulatedSubgroup: e.emulated_subgroup ?? null,
                                oldData: e.old_data,
                                newData: e.new_data,
                                changedFields: e.changed_fields,
                                description: null,
                                category: null,
                                changes: null,
                                dealName: e.deal_name ?? null,
                                serviceProviderName: e.service_provider_name ?? null,
                                period: e.period ?? null,
                                fileId: e.file_id ?? null,
                                fileName: e.file_name ?? null,
                            })),
                            total: response.total,
                            totalUnfiltered: response.total_unfiltered ?? response.total,
                            page: response.page,
                            pageSize: response.page_size,
                            hasMore: response.has_more ?? false,
                        },
                        error: null,
                    });
                } catch (error) {
                    // Swallow abort errors — the superseding request owns the loading state.
                    if (error instanceof DOMException && error.name === "AbortError") return;
                    setAuditState({
                        status: "rejected",
                        data: null,
                        error: error instanceof Error ? error : new Error(String(error)),
                    });
                }
            }, []);

            const table = useServerTable<TAuditLogQuery>({
                pageSize: 50,
                initialSort: [],
                toQuery: useCallback(
                    (input: TServerTableQueryInput) => toAuditLogQuery(input, true),
                    [],
                ),
                fetchData,
            });

            const onMount = useCallback(() => {
                api.auditLog
                    .filterOptionsGlobal()
                    .then((opts) =>
                        setFacetOptions({
                            deals: opts.deals,
                            businessPartners: opts.business_partners,
                            periods: opts.periods,
                            users: opts.users,
                            companies: opts.companies,
                        }),
                    )
                    .catch(() => undefined);
            }, []);

            return {
                onMount,
                onUnmount: NOOP,
                useAuditLogState: () => auditState as TAsyncState<(typeof auditState)["data"]>,
                onPageChange: table.setPage,
                currentPage: () => table.page,
                isGlobal: true,
                subgroupLabels,
                onDownloadFile: useCallback(async (fileId: string) => {
                    await api.files.downloadFile(fileId, "processed");
                }, []),
                columnFilters: table.columnFilters,
                onColumnFiltersChange: table.onColumnFiltersChange,
                sorting: table.sorting,
                onSortingChange: table.onSortingChange,
                facetOptions,
            };
        },
    });

    return { ViewAuditLog, ViewGlobalAuditLog };
}
